import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import adminOperationsService from '../services/adminOperationsService';

const number = (value) => Number(value || 0).toLocaleString();
const amount = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 8 });
const dateTime = (value) => value ? new Date(value).toLocaleString() : '—';

const statusStyle = {
  healthy: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
  waiting: 'bg-slate-500/15 text-slate-300 ring-slate-500/30',
  disabled: 'bg-slate-500/15 text-slate-400 ring-slate-500/30',
  stale: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
  critical: 'bg-red-500/15 text-red-300 ring-red-500/30',
};

const Pill = ({ status }) => (
  <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize ring-1 ${statusStyle[status] || statusStyle.waiting}`}>{status}</span>
);

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (quiet = false) => {
    quiet ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      const [overview, treasury] = await Promise.all([
        adminOperationsService.getOverview(),
        adminOperationsService.getTreasuryRisk(quiet),
      ]);
      setData({ ...overview, treasury });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load(true), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const summary = data?.summary || {};
  const cards = [
    ['Total users', summary.users, `${number(summary.new_users_24h)} joined in 24h`, '/admin/users', 'text-cyan-300'],
    ['Active users', summary.active_users, 'Enabled customer accounts', '/admin/users', 'text-emerald-300'],
    ['Open orders', summary.open_orders, `${number(summary.orders_24h)} created in 24h`, '/admin/orders', 'text-violet-300'],
    ['Completed orders', summary.completed_orders_24h, 'Completed in 24h', '/admin/orders', 'text-blue-300'],
    ['Pending withdrawals', summary.pending_withdrawals, 'Requires operations review', '/admin/withdrawals', summary.pending_withdrawals ? 'text-amber-300' : 'text-emerald-300'],
    ['Failed withdrawals', summary.failed_withdrawals_24h, 'Failures in 24h', '/admin/withdrawals', summary.failed_withdrawals_24h ? 'text-red-300' : 'text-emerald-300'],
    ['Wallet operations', summary.wallet_operations_24h, 'Ledger entries in 24h', '/admin/wallet-ledger', 'text-fuchsia-300'],
    ['Active markets', summary.enabled_pairs, `${number(summary.assets)} configured assets`, '/admin/coins-pairs', 'text-cyan-300'],
  ];

  return (
    <AdminLayout>
      <div className="space-y-7">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Live production data</p>
            <h1 className="mt-2 text-3xl font-bold text-white">Operations dashboard</h1>
            <p className="mt-2 text-sm text-slate-400">Users, money movement, order books, and infrastructure in one view.</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">Updated {dateTime(data?.generated_at)}</span>
            <button onClick={() => load(true)} disabled={refreshing} className="rounded-lg border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 hover:border-cyan-500 disabled:opacity-50">
              {refreshing ? 'Refreshing…' : 'Refresh data'}
            </button>
          </div>
        </div>

        {error && (
          <div role="alert" className="flex items-center justify-between rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-200">
            <span>{error}</span><button onClick={() => load()} className="rounded-md bg-red-500/20 px-3 py-1 text-sm">Retry</button>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => <div key={index} className="h-32 animate-pulse rounded-xl bg-slate-800" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {cards.map(([label, value, detail, href, color]) => (
                <Link to={href} key={label} className="rounded-xl border border-slate-800 bg-slate-950/70 p-5 transition hover:-translate-y-0.5 hover:border-slate-700">
                  <p className="text-sm font-medium text-slate-400">{label}</p>
                  <p className={`mt-3 text-3xl font-bold ${color}`}>{number(value)}</p>
                  <p className="mt-2 text-xs text-slate-500">{detail}</p>
                </Link>
              ))}
            </div>

            <section className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/70">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-800 px-5 py-4"><div><h2 className="font-semibold text-white">Treasury coverage</h2><p className="mt-1 text-xs text-slate-500">Customer liabilities and live keeper balances</p></div><Link to="/admin/keeper" className="text-sm text-cyan-400">Keeper controls →</Link></div>
              <div className="grid gap-px bg-slate-800 sm:grid-cols-4">
                {[
                  ['Customer liabilities', data?.treasury?.summary?.customer_liability_usdt, 'USDT priced'],
                  ['Keeper balances', data?.treasury?.summary?.keeper_balance_usdt, 'USDT priced'],
                  ['Coverage ratio', data?.treasury?.summary?.coverage_ratio, 'Keeper / liabilities'],
                  ['Low-balance assets', data?.treasury?.summary?.low_balance_assets, `${number(data?.treasury?.summary?.unavailable_assets)} unavailable`],
                ].map(([label, value, detail]) => <div key={label} className="bg-slate-950 p-4"><p className="text-xs uppercase tracking-wide text-slate-500">{label}</p><p className="mt-2 font-mono text-lg text-slate-100">{amount(value)}</p><p className="mt-1 text-xs text-slate-500">{detail}</p></div>)}
              </div>
              <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-800 text-left text-sm"><thead className="bg-slate-900/80 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-5 py-3">Asset</th><th className="px-5 py-3">Liability</th><th className="px-5 py-3">Keeper</th><th className="px-5 py-3">Pending withdrawals</th><th className="px-5 py-3">Coverage</th><th className="px-5 py-3">Status</th></tr></thead><tbody className="divide-y divide-slate-800">{(data?.treasury?.rows || []).map((row) => <tr key={row.symbol}><td className="px-5 py-3 font-semibold text-slate-100">{row.symbol}</td><td className="px-5 py-3 font-mono">{amount(row.customer_liability)}</td><td className="px-5 py-3 font-mono">{row.keeper_balance == null ? 'Unavailable' : amount(row.keeper_balance)}</td><td className="px-5 py-3 font-mono">{amount(row.pending_withdrawals)}</td><td className="px-5 py-3">{row.coverage_ratio == null ? '—' : `${amount(Number(row.coverage_ratio) * 100)}%`}</td><td className="px-5 py-3"><Pill status={row.error ? 'waiting' : row.low_balance ? 'critical' : 'healthy'} /></td></tr>)}</tbody></table></div>
            </section>

            <div className="grid gap-6 xl:grid-cols-3">
              <section className="rounded-xl border border-slate-800 bg-slate-950/70 xl:col-span-2">
                <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
                  <div><h2 className="font-semibold text-white">Operational alerts</h2><p className="mt-1 text-xs text-slate-500">Generated from current backend state</p></div>
                  <span className="rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-300">{data?.alerts?.length || 0}</span>
                </div>
                <div className="divide-y divide-slate-800">
                  {data?.alerts?.length ? data.alerts.map((alert, index) => (
                    <Link key={`${alert.title}-${index}`} to={alert.href || '/admin'} className="flex gap-4 p-5 transition hover:bg-slate-900">
                      <span className={`mt-1 h-2.5 w-2.5 flex-none rounded-full ${alert.severity === 'critical' ? 'bg-red-400' : 'bg-amber-400'}`} />
                      <div><p className="font-medium text-slate-100">{alert.title}</p><p className="mt-1 text-sm text-slate-400">{alert.message}</p></div>
                    </Link>
                  )) : <div className="p-8 text-center text-sm text-emerald-300">No operational alerts detected.</div>}
                </div>
              </section>

              <section className="rounded-xl border border-slate-800 bg-slate-950/70 p-5">
                <h2 className="font-semibold text-white">Infrastructure</h2>
                <p className="mt-1 text-xs text-slate-500">Checked {dateTime(data?.system?.checked_at)}</p>
                <div className="mt-5 space-y-3">
                  {[
                    ['Database', data?.system?.database],
                    ['Cache', data?.system?.cache],
                  ].map(([name, service]) => (
                    <div key={name} className="rounded-lg border border-slate-800 bg-slate-900 p-4">
                      <div className="flex items-center justify-between"><span className="text-sm text-slate-300">{name}</span><Pill status={service?.healthy ? 'healthy' : 'critical'} /></div>
                      <p className="mt-2 text-xs text-slate-500">Response {service?.response_ms ?? '—'} ms</p>
                      {service?.error && <p className="mt-2 break-words text-xs text-red-300">{service.error}</p>}
                    </div>
                  ))}
                  <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 p-4">
                    <span className="text-sm text-slate-300">Maintenance mode</span><Pill status={data?.system?.maintenance_mode ? 'stale' : 'healthy'} />
                  </div>
                </div>
              </section>
            </div>

            <section className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/70">
              <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4"><div><h2 className="font-semibold text-white">Order-book coverage</h2><p className="mt-1 text-xs text-slate-500">Cache freshness and open-order visibility by market</p></div><Link to="/admin/coins-pairs" className="text-sm text-cyan-400 hover:text-cyan-300">Manage markets →</Link></div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-800 text-left text-sm">
                  <thead className="bg-slate-900/80 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-5 py-3">Market</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Open orders</th><th className="px-5 py-3">Buy / sell levels</th><th className="px-5 py-3">Cache age</th></tr></thead>
                  <tbody className="divide-y divide-slate-800">
                    {(data?.orderbooks || []).map((pair) => <tr key={pair.id} className="hover:bg-slate-900/70"><td className="px-5 py-4 font-semibold text-slate-100">{pair.base?.symbol}-{pair.quote?.symbol}</td><td className="px-5 py-4"><Pill status={pair.orderbook?.status} /></td><td className="px-5 py-4 text-slate-300">{number(pair.open_orders)}</td><td className="px-5 py-4 text-slate-300">{number(pair.orderbook?.buy_levels)} / {number(pair.orderbook?.sell_levels)}</td><td className="px-5 py-4 text-slate-400">{pair.orderbook?.age_seconds == null ? 'No cache' : `${pair.orderbook.age_seconds}s`}</td></tr>)}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="grid gap-6 xl:grid-cols-2">
              <section className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/70">
                <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4"><h2 className="font-semibold text-white">Recent wallet activity</h2><Link to="/admin/wallet-ledger" className="text-sm text-cyan-400">Full ledger →</Link></div>
                <div className="divide-y divide-slate-800">
                  {(data?.recent_wallet_activity || []).slice(0, 7).map((item) => <div key={item.id} className="flex items-center justify-between gap-4 px-5 py-3"><div className="min-w-0"><p className="truncate text-sm text-slate-200">{item.user?.label || 'Unknown user'}</p><p className="text-xs text-slate-500">{item.operation} · {dateTime(item.created_at)}</p></div><div className="text-right"><p className="font-mono text-sm text-slate-100">{amount(item.amount)} {item.currency?.symbol}</p><p className="text-xs text-slate-500">{item.state_label}</p></div></div>)}
                  {!data?.recent_wallet_activity?.length && <p className="p-6 text-center text-sm text-slate-500">No wallet history available.</p>}
                </div>
              </section>
              <section className="overflow-hidden rounded-xl border border-slate-800 bg-slate-950/70">
                <div className="border-b border-slate-800 px-5 py-4"><h2 className="font-semibold text-white">Admin audit activity</h2></div>
                <div className="divide-y divide-slate-800">
                  {(data?.recent_admin_activity || []).slice(0, 7).map((item) => <div key={item.id} className="px-5 py-3"><div className="flex justify-between gap-3"><p className="truncate text-sm text-slate-200">{item.object}</p><span className="text-xs text-slate-500">{dateTime(item.created_at)}</span></div><p className="mt-1 text-xs text-slate-500">{item.user || 'System'} · {item.model || 'record'}</p></div>)}
                  {!data?.recent_admin_activity?.length && <p className="p-6 text-center text-sm text-slate-500">No audit entries available.</p>}
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
